/**
 * Enhanced Receipt Processor V2 - Handles multiple receipt types with better accuracy
 * Includes proper VAT handling for different product categories
 */
export class EnhancedReceiptProcessor {
  constructor() {
    // Irish VAT rates by category
    this.vatRates = {
      'clothing': 0.0,  // 0% VAT on clothing in Ireland
      'beauty': 0.23,   // 23% VAT on beauty/cosmetics
      'food': 0.0,      // 0% VAT on most food
      'books': 0.0,     // 0% VAT on books
      'medical': 0.0,   // 0% VAT on medical
      'supplies': 0.23, // 23% VAT on general supplies
      'electronics': 0.23, // 23% VAT on electronics
      'household': 0.23, // 23% VAT on household items
      'travel': 0.23    // 23% VAT on fuel/travel
    };

    // Keywords for category detection
    this.categoryKeywords = {
      'clothing': [
        'tee', 'shirt', 'dress', 'trouser', 'jean', 'skirt', 'coat', 'jacket', 'hoodie', 
        'sweater', 'cardigan', 'blouse', 'top', 'bottom', 'short', 'pant', 'sock', 
        'underwear', 'bra', 'brief', 'legging', 'jogger', 'vest', 'polo', 'tank', 
        'cami', 'blazer', 'suit', 'tie', 'scarf', 'hat', 'cap', 'glove', 'boot', 
        'shoe', 'sandal', 'trainer', 'heel', 'bag', 'purse', 'wallet', 'belt', 
        'watch', 'sunglass', 'jewel'
      ],
      'beauty': [
        'cream', 'lotion', 'serum', 'foundation', 'mascara', 'lipstick', 'shampoo', 
        'conditioner', 'soap', 'perfume', 'cologne', 'deodorant', 'moisturizer', 
        'cleanser', 'toner', 'makeup', 'nail', 'polish', 'eyeshadow', 'blush', 
        'concealer', 'primer', 'bronzer', 'highlighter'
      ],
      'food': [
        'bread', 'milk', 'cheese', 'meat', 'fruit', 'vegetable', 'drink', 'water', 
        'juice', 'coffee', 'tea', 'chocolate', 'biscuit', 'cake'
      ]
    };

    // Vendor-specific processors
    this.vendorProcessors = {
      'penneys': this.processPenneysReceipt.bind(this),
      'primark': this.processPenneysReceipt.bind(this),
      'boots': this.processBootsReceipt.bind(this),
      'pharmacy': this.processPharmacyReceipt.bind(this),
      'tesco': this.processGenericReceipt.bind(this),
      'supervalu': this.processGenericReceipt.bind(this),
      'dunnes': this.processGenericReceipt.bind(this)
    };
  }

  /**
   * Main processing function - routes to appropriate processor
   */
  processReceipt(text, metadata = {}) {
    console.log('≡ƒöä Processing receipt with enhanced processor V2');
    const detectedVendor = this.detectVendor(text).toLowerCase();
    console.log(`≡ƒÅ¬ Detected vendor: ${detectedVendor}`);

    const detectedDate = this.extractDate(text) || new Date().toISOString().split('T')[0];
    const detectedAmount = this.extractTotalAmount(text);
    const detectedVendorName = this.formatVendorName(detectedVendor);

    const metadataDate = typeof metadata?.date === 'string' && metadata.date.trim() ? metadata.date : null;
    const metadataAmount = Number.parseFloat(metadata?.amount);
    const metadataVendor = typeof metadata?.vendor === 'string' ? metadata.vendor.trim() : '';
    const metadataCategory = typeof metadata?.category === 'string' ? metadata.category.trim() : '';
    const metadataCurrency = typeof metadata?.currency === 'string' ? metadata.currency : '';
    const metadataVatAmount = Number.parseFloat(metadata?.vatAmount);
    const metadataVatRate = Number.parseFloat(metadata?.vatRate);

    // Only trust upstream amount/VAT values when they are valid positive numbers.
    const resolvedAmount = Number.isFinite(metadataAmount) && metadataAmount > 0
      ? metadataAmount
      : detectedAmount;
    const resolvedVatAmount = Number.isFinite(metadataVatAmount) && metadataVatAmount > 0
      ? metadataVatAmount
      : 0;
    const resolvedVatRate = Number.isFinite(metadataVatRate) && metadataVatRate > 0
      ? metadataVatRate
      : 0;

    const normalizedMetadataVendor = metadataVendor.toLowerCase();
    const metadataLooksLikeGroceryLocation =
      /\b(tesco|supervalu|dunnes|lidl|aldi|spar|centra|m&s|marks\s*&\s*spencer|iceland)\b/i.test(normalizedMetadataVendor);
    const shouldUseMetadataVendor =
      metadataVendor &&
      normalizedMetadataVendor !== 'unknown vendor' &&
      normalizedMetadataVendor !== 'unknown' &&
      !(detectedVendor === 'pharmacy' && metadataLooksLikeGroceryLocation);
    const resolvedVendor = shouldUseMetadataVendor ? metadataVendor : detectedVendorName;
    const groceryVendorPattern = /\b(tesco|supervalu|dunnes|lidl|aldi|spar|centra|m&s|marks\s*&\s*spencer|iceland)\b/i;
    const isGroceryVendor =
      groceryVendorPattern.test(String(resolvedVendor || '').toLowerCase()) ||
      groceryVendorPattern.test(String(detectedVendor || '').toLowerCase());

    let resolvedCategory = metadataCategory || 'supplies';
    if (String(resolvedCategory).toLowerCase() === 'utilities' && isGroceryVendor) {
      resolvedCategory = 'misc';
    }

    // Initialize base data
    let extractedData = {
      ...metadata,
      date: metadataDate || detectedDate,
      amount: resolvedAmount,
      vendor: resolvedVendor,
      category: resolvedCategory,
      items: Array.isArray(metadata?.items) ? metadata.items : [],
      vatAmount: resolvedVatAmount,
      vatRate: resolvedVatRate,
      currency: metadataCurrency || '€'
    };

    // Route to appropriate processor
    const processor = this.vendorProcessors[detectedVendor] || this.processGenericReceipt.bind(this);
    extractedData = processor(text, extractedData);

    console.log('Γ£à Receipt processing complete:', {
      vendor: extractedData.vendor,
      amount: extractedData.amount,
      itemCount: extractedData.items.length,
      category: extractedData.category
    });

    return extractedData;
  }

  /**
   * Process Penneys/Primark receipts - handles clothing items correctly
   */
  processPenneysReceipt(text, extractedData) {
    console.log('≡ƒæò Processing Penneys/Primark receipt');
    extractedData.vendor = 'Penneys';
    // Use broad default; individual items will carry their own categories
    extractedData.category = 'misc';

    // If upstream (client OCR) already provided items, keep them instead of re-parsing
    if (extractedData.items && extractedData.items.length > 0) {
      console.log('≡ƒº╡ Using pre-extracted Penneys items from OCR metadata');
      const normalizedItems = extractedData.items.map(item => this.normalizePenneysItem(item));

      extractedData.items = normalizedItems;

      // If total is missing/zero, derive it from the items we already have
      const itemsTotal = normalizedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      if (!extractedData.amount || extractedData.amount === 0) {
        extractedData.amount = parseFloat(itemsTotal.toFixed(2));
      }

      extractedData.vatAmount = 0;
      extractedData.vatRate = 0;
      extractedData.vatComponents = this.buildVatFromItems(normalizedItems);
      return extractedData;
    }

    // Extract items from Penneys receipt format
    const items = this.extractPenneysItems(text);
    if (items.length > 0) {
      extractedData.items = items;

      // Recalculate total and VAT based on items
      const itemsTotal = items.reduce((sum, item) => sum + item.amount, 0);
      const totalVat = items.reduce((sum, item) => sum + (item.vatAmount || 0), 0);

      // Use extracted total if reasonable, otherwise use items total
      if (Math.abs(extractedData.amount - itemsTotal) > 2.0) {
        console.log(`ΓÜá∩╕Å Total mismatch: Receipt=${extractedData.amount}, Items=${itemsTotal}`);
        // Keep receipt total but adjust proportionally if needed
      }

      extractedData.vatAmount = parseFloat(totalVat.toFixed(2));
      extractedData.vatRate = totalVat > 0 ? 0.23 : 0.0;

      // Keep main category broad; items carry specifics
      extractedData.category = 'misc';
    } else {
      // Fallback: create single clothing item
      console.log('≡ƒöä Creating fallback clothing item');
      const vatRate = 0.0; // Clothing is 0% VAT
      extractedData.items = [{
        description: 'Clothing Items',
        quantity: 1,
        unitPrice: extractedData.amount,
        amount: extractedData.amount,
        category: 'clothing',
        vatRate: vatRate,
        vatAmount: 0,
        vatReclaimable: false
      }];
      extractedData.vatAmount = 0;
      extractedData.vatRate = 0;
    }

    extractedData.vatComponents = this.buildVatFromItems(extractedData.items);
    return extractedData;
  }

  /**
   * Extract individual items from Penneys receipt
   */
  extractPenneysItems(text) {
    const items = [];
    const lines = text.split('\n').map(line => line.trim());
    console.log('≡ƒôï Extracting items from Penneys receipt');

    // Find the items section (between "SALE" and "Total")
    let itemsStartIndex = -1;
    let itemsEndIndex = lines.length;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^SALE$/i.test(line)) {
        itemsStartIndex = i + 1;
      }
      if (/^Total$/i.test(line) || /^Γé¼\s*\d+\.\d{2}$/i.test(line)) {
        itemsEndIndex = i;
        break;
      }
    }

    console.log(`≡ƒôì Items section: lines ${itemsStartIndex} to ${itemsEndIndex}`);
    if (itemsStartIndex === -1) return items;

    const itemLines = lines.slice(itemsStartIndex, itemsEndIndex);

    // Process item lines - Penneys format: "ITEM_CODE DESCRIPTION" followed by price
    for (let i = 0; i < itemLines.length - 1; i++) {
      const line = itemLines[i];
      const nextLine = itemLines[i + 1];

      // Skip if current line is a price
      if (/^\d+\.\d{2}$/.test(line)) continue;

      // Check if next line is a price
      const priceMatch = nextLine?.match(/^(\d+\.\d{2})$/);
      if (priceMatch) {
        const price = parseFloat(priceMatch[1]);
        const description = this.cleanPenneysDescription(line);
        if (description && price > 0) {
          const item = this.normalizePenneysItem({
            description: description,
            amount: price,
            unitPrice: price
          });

          items.push(item);

          console.log(`Added item: ${description} - EUR ${price} (${item.category}, ${(item.vatRate*100)}% VAT)`);
          // Skip the price line in next iteration
          i++;
        }
      }
    }

    return items;
  }

  /**
   * Clean up Penneys item descriptions
   */
  cleanPenneysDescription(description) {
    if (!description) return '';
    
    return description
      .trim()
      .replace(/^\d+\s+/, '')  // Remove leading item codes
      .replace(/^[A-Z0-9]+\s+/, '')  // Remove product codes
      .replace(/:\s*[A-Z]+:\s*[A-Z]+$/i, '')  // Remove color/size info at end
      .replace(/:\s*[A-Z]+$/i, '')  // Remove single trailing descriptor
      .trim();
  }

  processPharmacyReceipt(text, extractedData) {
    console.log('Processing pharmacy receipt');
    const amount = extractedData.amount || this.extractTotalAmount(text) || 0;
    const vendor = this.extractPharmacyVendorName(text) || 'Pharmacy';
    const hasScripts = /\b(scripts?|prescriptions?|medicine|medication|rx)\b/i.test(text || '');

    extractedData.vendor = vendor;
    extractedData.category = 'medical_expenses';
    extractedData.amount = amount;
    extractedData.vatAmount = 0;
    extractedData.vatRate = 0;
    extractedData.vatComponents = [];
    extractedData.items = [{
      description: hasScripts ? 'Scripts' : 'Pharmacy Purchase',
      quantity: 1,
      unitPrice: amount,
      amount,
      category: 'medical_expenses',
      vatRate: 0,
      vatAmount: 0,
      vatReclaimable: false
    }];

    return extractedData;
  }

  // Normalize Penneys/Primark items and map to safer categories
  normalizePenneysItem(item = {}) {
    const amount = item.amount ?? item.unitPrice ?? 0;
    const desc = (item.description || '').toLowerCase();

    let category = 'misc';
    const suppliesKeywords = ['cotton', 'bud', 'towel', 'makeup remover', 'remover', 'wipe', 'cotton pad', 'cleanser', 'sanitizer', 'disinfect', 'glove'];
    const workwearKeywords = ['black tee', 'black t-shirt', 'black shirt', 'black pant', 'uniform', 'workwear', 'apron'];
    const clothingKeywords = ['dress', 'shirt', 'pant', 'jean', 'skirt', 'sock', 'tshirt', 't-shirt', 'top', 'hoodie', 'jacket', 'coat', 'shoe', 'trainer', 'hat', 'short', 'bra', 'underwear'];

    if (suppliesKeywords.some(k => desc.includes(k)) || workwearKeywords.some(k => desc.includes(k))) {
      category = 'supplies';
    } else if (clothingKeywords.some(k => desc.includes(k))) {
      category = 'clothing';
    }

    const vatRate = category === 'clothing' ? 0 : 0.23;
    const vatAmount = vatRate > 0 ? parseFloat((amount - (amount / (1 + vatRate))).toFixed(2)) : 0;

    return {
      description: item.description || 'Item',
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice ?? amount,
      amount,
      category,
      vatRate,
      vatAmount,
      vatReclaimable: vatRate > 0
    };
  }

  // Build VAT components from items
  buildVatFromItems(items = []) {
    if (!items.length) return [];
    const byRate = {};
    items.forEach(it => {
      const rate = typeof it.vatRate === 'number' ? it.vatRate : 0;
      const amt = typeof it.vatAmount === 'number' ? it.vatAmount : 0;
      if (!byRate[rate]) byRate[rate] = 0;
      byRate[rate] += amt;
    });
    return Object.entries(byRate).map(([rate, amt]) => ({
      rate: Number(rate),
      amount: parseFloat(amt.toFixed(2)),
      description: `${(Number(rate) * 100).toFixed(0)}% VAT`
    }));
  }

  /**
   * Enhanced Boots receipt processing
   */
  processBootsReceipt(text, extractedData) {
    console.log('≡ƒÆä Processing Boots receipt');
    extractedData.vendor = 'Boots';
    extractedData.category = 'beauty';

    const items = this.extractBootsItems(text);
    if (items.length > 0) {
      extractedData.items = items;
      const totalVat = items.reduce((sum, item) => sum + (item.vatAmount || 0), 0);
      extractedData.vatAmount = parseFloat(totalVat.toFixed(2));
      extractedData.vatRate = 0.23;
    } else {
      // Fallback beauty item
      const vatRate = 0.23;
      const vatAmount = parseFloat((extractedData.amount - (extractedData.amount / (1 + vatRate))).toFixed(2));
      extractedData.items = [{
        description: 'Beauty Products',
        quantity: 1,
        unitPrice: extractedData.amount,
        amount: extractedData.amount,
        category: 'beauty',
        vatRate: vatRate,
        vatAmount: vatAmount,
        vatReclaimable: true
      }];
      extractedData.vatAmount = vatAmount;
      extractedData.vatRate = vatRate;
    }

    this.updateVatComponents(extractedData);
    return extractedData;
  }

  /**
   * Extract items from Boots receipt
   */
  extractBootsItems(text) {
    const items = [];
    const lines = text.split('\n').map(line => line.trim());

    // Implementation similar to previous Boots processor but simplified
    // Focus on finding product description + price pairs
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i];
      const nextLine = lines[i + 1];

      // Skip non-product lines
      if (this.isExcludedLine(line)) continue;

      // Check for product + price pattern
      if (this.looksLikeBootsProduct(line) && /^\d+\.\d{2}$/.test(nextLine)) {
        const price = parseFloat(nextLine);
        const description = this.cleanProductDescription(line);
        if (price > 0 && price <= 200) { // Reasonable price range
          const vatRate = 0.23;
          const vatAmount = parseFloat((price - (price / (1 + vatRate))).toFixed(2));
          
          items.push({
            description: description,
            quantity: 1,
            unitPrice: price,
            amount: price,
            category: 'beauty',
            vatRate: vatRate,
            vatAmount: vatAmount,
            vatReclaimable: true
          });
          
          i++; // Skip price line
        }
      }
    }

    return items;
  }

  /**
   * Generic receipt processing
   */
  processGenericReceipt(text, extractedData) {
    console.log('≡ƒôä Processing generic receipt');

    // Try to extract some basic items
    const items = this.extractGenericItems(text);
    if (items.length === 0) {
      // Create single item
      const category = extractedData.category || 'supplies';
      const vatRate = this.vatRates[category] || 0.23;
      const vatAmount = parseFloat((extractedData.amount * vatRate / (1 + vatRate)).toFixed(2));
      
      items.push({
        description: `${extractedData.vendor} Purchase`,
        quantity: 1,
        unitPrice: extractedData.amount,
        amount: extractedData.amount,
        category: category,
        vatRate: vatRate,
        vatAmount: vatAmount,
        vatReclaimable: vatRate > 0
      });
    }

    extractedData.items = items;
    const totalVat = items.reduce((sum, item) => sum + (item.vatAmount || 0), 0);
    extractedData.vatAmount = parseFloat(totalVat.toFixed(2));
    this.updateVatComponents(extractedData);
    
    return extractedData;
  }

  /**
   * Categorize an item based on its description
   */
  categorizeItem(description) {
    console.log(`≡ƒöì Categorizing: "${description}"`);
    
    // Clean the description for better matching
    const cleanDesc = description.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ') // Remove special chars
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
    console.log(`≡ƒº╣ Cleaned: "${cleanDesc}"`);
    
    // CLOTHING PATTERNS - Using regex for precise matching
    const clothingPatterns = [
      /\btee\b/, // TEE
      /\bt-shirt\b/, // T-SHIRT
      /\bshirt\b/, // SHIRT
      /\btop\b/, // TOP
      /\bblouse\b/, // BLOUSE
      /\btank\b/, // TANK
      /\bvest\b/, // VEST
      /\bsweater\b/, // SWEATER
      /\bhoodie\b/, // HOODIE
      /\bcardigan\b/, // CARDIGAN
      /\bpolo\b/, // POLO
      /\btunic\b/, // TUNIC
      /\bpullover\b/, // PULLOVER
      /\bjumper\b/, // JUMPER
      /\bsweatshirt\b/, // SWEATSHIRT
      /\bjersey\b/, // JERSEY
      
      // SPECIAL PATTERNS for abbreviated items
      /jacquard\s+t(\s|$)/, // "JACQUARD T" - t-shirt
      /texture\s+t(\s|$)/, // "TEXTURE T" - t-shirt
      /\bt(\s|$)/, // Just "T" at end - likely t-shirt
      /opp\s+tee/, // "OPP TEE"
      
      // BOTTOMS
      /\bjean/, // JEAN, JEANS
      /\btrouser/, // TROUSER, TROUSERS
      /\bpant/, // PANT, PANTS
      /\bshort/, // SHORT, SHORTS
      /\bcargo/, // CARGO (shorts/pants) - THIS IS KEY!
      /\bchino/, // CHINO
      /\blegging/, // LEGGING, LEGGINGS
      /\btight/, // TIGHT, TIGHTS
      /\bskirt/, // SKIRT
      /\bdress/, // DRESS
      /\bjogger/, // JOGGER, JOGGERS
      /\btrack/, // TRACK (pants)
      /\bsweat/, // SWEAT (pants)
      /\bbrief/, // BRIEF, BRIEFS
      /\bboxer/, // BOXER, BOXERS
      /\bunderwear/, // UNDERWEAR
      /\blingerie/, // LINGERIE
      
      // OUTERWEAR
      /\bjacket/, // JACKET
      /\bcoat/, // COAT
      /\bblazer/, // BLAZER
      /\bparka/, // PARKA
      /\bwindbreaker/, // WINDBREAKER
      /\bbomber/, // BOMBER
      /\bdenim/, // DENIM
      /\bleather/, // LEATHER
      /\brain/, // RAIN (coat)
      /\bwinter/, // WINTER (coat)
      /\bpuffer/, // PUFFER
      /\btrench/, // TRENCH
      
      // FOOTWEAR & ACCESSORIES
      /\bshoe/, // SHOE, SHOES
      /\bboot/, // BOOT, BOOTS
      /\bsandal/, // SANDAL, SANDALS
      /\bsneaker/, // SNEAKER, SNEAKERS
      /\bheel/, // HEEL, HEELS
      /\bflat/, // FLAT, FLATS
      /\btrainer/, // TRAINER, TRAINERS
      /\brunner/, // RUNNER, RUNNERS
      /\bsock/, // SOCK, SOCKS
      /\bstocking/, // STOCKING, STOCKINGS
      /\bscarf/, // SCARF
      /\bhat/, // HAT
      /\bcap/, // CAP
      /\bglove/, // GLOVE, GLOVES
      /\bbelt/, // BELT
      /\bhandbag/, // HANDBAG
      /\bpurse/, // PURSE
      /\bwallet/, // WALLET
      /\bbackpack/, // BACKPACK
    ];
    
    // SUPPLIES PATTERNS - Check these BEFORE beauty patterns
    const suppliesPatterns = [
      /\bpaper\s+bag/, // PAPER BAG - this should be supplies!
      /\bbag\s+(ro|re|receipt)/, // Receipt bags
      /\bbox/, // BOX
      /\bcontainer/, // CONTAINER
      /\bstorage/, // STORAGE
      /\boffice/, // OFFICE
      /\bpen/, // PEN
      /\bpencil/, // PENCIL
      /\bnotebook/, // NOTEBOOK
      /\bfolder/, // FOLDER
      /\bfile/, // FILE
      /\bstapler/, // STAPLER
      /\btape/, // TAPE
      /\bglue/, // GLUE
      /\bcalculator/, // CALCULATOR
      /\bcleaning/, // CLEANING
      /\bdisinfectant/, // DISINFECTANT
      /\bsanitizer/, // SANITIZER
      /\bsoap/, // SOAP (unless beauty context)
      /\bdetergent/, // DETERGENT
    ];
    
    // BEAUTY/COSMETICS PATTERNS
    const beautyPatterns = [
      /\bmakeup/, // MAKEUP
      /\bcosmetic/, // COSMETIC, COSMETICS
      /\blipstick/, // LIPSTICK
      /\bmascara/, // MASCARA
      /\bfoundation/, // FOUNDATION
      /\bconcealer/, // CONCEALER
      /\bpowder/, // POWDER
      /\bblush/, // BLUSH
      /\bbronzer/, // BRONZER
      /\beyeshadow/, // EYESHADOW
      /\beyeliner/, // EYELINER
      /\bnail\s+polish/, // NAIL POLISH
      /\bperfume/, // PERFUME
      /\bfragrance/, // FRAGRANCE
      /\bserum/, // SERUM
      /\bmoisturizer/, // MOISTURIZER
      /\bcleanser/, // CLEANSER
      /\btoner/, // TONER
      /\bcream/, // CREAM
      /\blotion/, // LOTION
      /\bshampoo/, // SHAMPOO
      /\bconditioner/, // CONDITIONER
    ];
    
    // BEAUTY SUPPLIES/TOOLS PATTERNS
    const beautySuppliesPatterns = [
      /\bcotton\s+pad/, // COTTON PAD, COTTON PADS
      /\bcotton\s+swab/, // COTTON SWAB, COTTON SWABS
      /\btissue/, // TISSUE, TISSUES
      /\bwipe/, // WIPE, WIPES
      /\btowel/, // TOWEL, TOWELS (unless hair towel)
      /\bcloth/, // CLOTH, CLOTHS
      /\bsponge/, // SPONGE, SPONGES
      /\bapplicator/, // APPLICATOR, APPLICATORS
      /\bbrush\s+set/, // BRUSH SET
      /\bmakeup\s+brush/, // MAKEUP BRUSH
      /\btweezers/, // TWEEZERS
      /\bscissors/, // SCISSORS
      /\bfile/, // FILE (nail)
      /\bbuffer/, // BUFFER
      /\bcuticle/, // CUTICLE
      /\borganizer/, // ORGANIZER
      /\bcase/, // CASE
      /\bholder/, // HOLDER
      /\bdispenser/, // DISPENSER
      /\bpump/, // PUMP
      /\bbottle/, // BOTTLE
      /\bjar/, // JAR
      /\btube/, // TUBE
      /\bcompact/, // COMPACT
      /\btray/, // TRAY
      /\bbasket/, // BASKET
      /\bbin/, // BIN
      /\bshelf/, // SHELF
    ];
    
    // Check patterns in order of specificity
    // 1. Check for SUPPLIES first (highest priority for specific items like paper bags)
    for (const pattern of suppliesPatterns) {
      if (pattern.test(cleanDesc)) {
        const match = cleanDesc.match(pattern);
        console.log(`≡ƒôª Supplies match: "${match[0]}" in "${description}"`);
        return 'supplies';
      }
    }
    
    // 2. Check for CLOTHING (most common in Penneys)
    for (const pattern of clothingPatterns) {
      if (pattern.test(cleanDesc)) {
        const match = cleanDesc.match(pattern);
        console.log(`≡ƒæù Clothing match: "${match[0]}" in "${description}"`);
        return 'clothing';
      }
    }
    
    // 3. Check for BEAUTY/COSMETICS
    for (const pattern of beautyPatterns) {
      if (pattern.test(cleanDesc)) {
        const match = cleanDesc.match(pattern);
        console.log(`≡ƒÆä Beauty match: "${match[0]}" in "${description}"`);
        return 'beauty';
      }
    }
    
    // 4. Check for BEAUTY SUPPLIES/TOOLS
    for (const pattern of beautySuppliesPatterns) {
      if (pattern.test(cleanDesc)) {
        const match = cleanDesc.match(pattern);
        console.log(`≡ƒº┤ Beauty supplies match: "${match[0]}" in "${description}"`);
        return 'beauty_supplies';
      }
    }
    
    // 5. DEFAULT: For Penneys receipts, assume clothing if no specific match
    console.log(`Γ¥ô No specific match for "${description}" - defaulting to clothing (Penneys store)`);
    return 'clothing';
  }

  /**
   * Detect vendor from receipt text
   */
  detectVendor(text) {
    if (this.extractPharmacyVendorName(text)) {
      return 'pharmacy';
    }

    const vendorPatterns = {
      'penneys': /penneys|primark/i,
      'boots': /boots\s+retail/i,
      'gelbottle': /the\s*gel\s*bottle|gel\s*bottle|gelbottle/i,
      'tesco': /tesco/i,
      'supervalu': /supervalu/i,
      'dunnes': /dunnes/i,
      'lidl': /lidl/i,
      'aldi': /aldi/i
    };
    
    for (const [vendor, pattern] of Object.entries(vendorPatterns)) {
      if (pattern.test(text)) {
        return vendor;
      }
    }
    
    return 'unknown';
  }

  extractPharmacyVendorName(text) {
    if (!text) return null;

    const lines = String(text)
      .split('\n')
      .map((line) => line.trim().replace(/\s{2,}/g, ' '))
      .filter(Boolean);

    const pharmacyLine = lines.find((line) =>
      /\bpharmacy\b/i.test(line) &&
      !/^\b(pharmacy|chemist)\b$/i.test(line)
    );

    return pharmacyLine || null;
  }

  /**
   * Extract total amount from receipt
   */
  extractTotalAmount(text) {
    if (!text) return 0;

    const parseAmountToken = (rawToken, assumeCents = false) => {
      if (!rawToken) return null;

      const tokenSource = String(rawToken).trim();
      const normalizedDigits = tokenSource.replace(/[^\d.\s,]/g, '');
      if (/^\d{4}[.\s]\d{2}$/.test(normalizedDigits)) {
        const yearCandidate = parseInt(normalizedDigits.slice(0, 4), 10);
        if (yearCandidate >= 1900 && yearCandidate <= 2100) {
          // Reject year-like tokens such as "2026 10" (often from date/time OCR).
          return null;
        }
      }

      let normalized = String(rawToken)
        .replace(/[€£$]/g, '')
        .trim();

      // OCR occasionally returns decimal as space, e.g. "62 13"
      if (/^\d+\s\d{2}$/.test(normalized)) {
        normalized = normalized.replace(/\s/, '.');
      }

      normalized = normalized.replace(/\s+/g, '');
      if (!normalized) return null;

      if (assumeCents && /^\d{3,8}$/.test(normalized)) {
        normalized = `${normalized.slice(0, -2)}.${normalized.slice(-2)}`;
      } else if (normalized.includes(',') && normalized.includes('.')) {
        if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
          normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else {
          normalized = normalized.replace(/,/g, '');
        }
      } else if (normalized.includes(',')) {
        const parts = normalized.split(',');
        if (parts[parts.length - 1].length === 2) {
          normalized = normalized.replace(/\./g, '').replace(',', '.');
        } else {
          normalized = normalized.replace(/,/g, '');
        }
      }

      const amount = parseFloat(normalized);
      if (!Number.isFinite(amount) || amount <= 0 || amount >= 100000) {
        return null;
      }
      return parseFloat(amount.toFixed(2));
    };

    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const candidates = [];
    const totalLinePattern = /\b(grand\s*total|total\s*due|amount\s*due|amount\s*payable|balance\s*due|total\s*to\s*pay|total|paid|card\s*sales)\b/i;
    const nonTotalMoneyPattern = /tax\s*excl|total\s*tax|vat\s*rate|base\s*price|shipping|products|subtotal/i;
    const discountMoneyPattern = /total\s*savings|savings?|coupon|discount|change|refund|cashback|points?/i;
    const paymentProofPattern = /debit\s*payment|credit\s*payment|visa|mastercard|pin verified|approved|you will be debited by/i;
    const receiptMetaPattern = /\b(date|time|store|checkout|trans|transaction|aid|auth|authorisation|approval|pan|sequence|clubcard|points|vat number|vat no)\b/i;
    const dateTimeValuePattern = /\b\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}\b|\b\d{4}[\/.-]\d{1,2}[\/.-]\d{1,2}\b|\b\d{1,2}:\d{2}\b/;

    const pushCandidate = (amount, score, line) => {
      if (!Number.isFinite(amount) || amount <= 0 || amount >= 100000) return;
      candidates.push({ amount, score, line });
    };

    lines.forEach((line, index) => {
      const lowerLine = line.toLowerCase();
      const explicitMoneyTokens = line.match(/[-+]?(?:[€£$]\s*)?\d{1,3}(?:[.,]\d{3})*[.,]\d{2}|[-+]?(?:[€£$]\s*)?\d+[.,]\d{2}/g) || [];

      explicitMoneyTokens.forEach((token) => {
        const amount = parseAmountToken(token);
        if (!Number.isFinite(amount)) return;

        let score = 0;
        if (totalLinePattern.test(lowerLine)) score += 150;
        if (nonTotalMoneyPattern.test(lowerLine)) score -= 100;
        if (discountMoneyPattern.test(lowerLine)) score -= 220;
        if (paymentProofPattern.test(lowerLine)) score += 70;
        if (receiptMetaPattern.test(lowerLine)) score -= 90;
        if (dateTimeValuePattern.test(lowerLine)) score -= 120;
        if (/[€£$]/.test(token)) score += 20;
        if (index >= lines.length * 0.65) score += 20;
        if (amount < 1 && totalLinePattern.test(lowerLine)) score -= 120;
        if (amount >= 1000 && !totalLinePattern.test(lowerLine)) score -= 120;

        pushCandidate(amount, score, line);
      });

      // Strong fallback: total on one line, amount on the next line.
      if (totalLinePattern.test(line)) {
        const nextLine = lines[index + 1] || '';
        const nextLineTokenMatch = nextLine.match(/[-+]?(?:[€£$]\s*)?\d{1,3}(?:[.,]\d{3})*[.,]\d{2}|[-+]?(?:[€£$]\s*)?\d+\s\d{2}|[-+]?(?:[€£$]\s*)?\d+[.,]\d{2}/);
        if (nextLineTokenMatch) {
          const nextAmount = parseAmountToken(nextLineTokenMatch[0]);
          if (Number.isFinite(nextAmount)) {
            let nextLineScore = 240;
            if (paymentProofPattern.test(lowerLine) || paymentProofPattern.test(nextLine.toLowerCase())) nextLineScore += 60;
            if (discountMoneyPattern.test(nextLine.toLowerCase())) nextLineScore -= 160;
            pushCandidate(nextAmount, nextLineScore, `${line} | ${nextLine}`);
          }
        }

        // Fallback for OCR output with no decimal separator, e.g. "Total 6213" => 62.13
        const integerTokens = line.match(/\b\d{3,8}\b/g) || [];
        integerTokens.forEach((token) => {
          const amount = parseAmountToken(token, true);
          if (!Number.isFinite(amount)) return;
          let score = 60;
          if (/grand\s*total|total\s*due|amount\s*due|balance\s*due/.test(lowerLine)) score += 40;
          pushCandidate(amount, score, line);
        });
      }
    });

    if (candidates.length > 0) {
      candidates.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return b.amount - a.amount;
      });
      return candidates[0].amount;
    }

    // Final fallback: any monetary value in document
    const globalMatches = text.match(/[-+]?(?:[€£$]\s*)?\d{1,3}(?:[.,\s]\d{3})*[.,]\d{2}|[-+]?(?:[€£$]\s*)?\d+[.,]\d{2}/g) || [];
    const globalAmounts = globalMatches
      .map((token) => parseAmountToken(token))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => b - a);

    return globalAmounts[0] || 0;
  }

  /**
   * Extract date from receipt
   */
  extractDate(text) {
    const datePatterns = [
      /Date:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/,
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/
    ];
    
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        return `${year}-${month}-${day}`;
      }
    }
    
    return null;
  }

  /**
   * Helper functions
   */
  formatVendorName(vendor) {
    const names = {
      'penneys': 'Penneys',
      'primark': 'Penneys',
      'boots': 'Boots',
      'gelbottle': 'GelBottle',
      'pharmacy': 'Pharmacy',
      'tesco': 'Tesco',
      'supervalu': 'SuperValu',
      'dunnes': 'Dunnes Stores'
    };
    
    return names[vendor] || vendor.charAt(0).toUpperCase() + vendor.slice(1);
  }

  getMostCommonCategory(categories) {
    const counts = {};
    categories.forEach(cat => counts[cat] = (counts[cat] || 0) + 1);
    return Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b, 'supplies');
  }

  isExcludedLine(line) {
    const excludePatterns = [
      /total|payment|card|visa|mastercard/i,
      /store|till|trans|date|time/i,
      /auth|ref|merchant/i,
      /\*{4,}/,
      /^\d{3,}$/
    ];
    
    return excludePatterns.some(pattern => pattern.test(line));
  }

  looksLikeBootsProduct(line) {
    return line.length > 3 && line.length < 50 && /[A-Za-z]/.test(line) && !this.isExcludedLine(line);
  }

  cleanProductDescription(description) {
    return description
      .trim()
      .replace(/^\d+\s+/, '')
      .replace(/[Γé¼┬ú$]\d+\.\d{2}$/, '')
      .trim();
  }

  extractGenericItems(text) {
    // Basic implementation for other receipts
    return [];
  }

  updateVatComponents(extractedData) {
    if (extractedData.vatAmount > 0) {
      extractedData.vatComponents = [{
        rate: extractedData.vatRate,
        amount: extractedData.vatAmount,
        description: `Standard Rate (${(extractedData.vatRate * 100).toFixed(0)}%)`
      }];
    } else {
      extractedData.vatComponents = [{
        rate: 0,
        amount: 0,
        description: 'Zero Rate (0%) - Clothing'
      }];
    }
  }
}

// Export the class
export default EnhancedReceiptProcessor;
